import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { getNextLevelProgressPercent, isAtTopLevel, IMyNetworkScoreSummaryResponse } from '../../interfaces/INetworkScore';
import { LevelDisc } from './LevelDisc';
import { NetworkScoreChecklist } from './NetworkScoreChecklist';

interface NetworkScoreDetailsProps {
    summary: IMyNetworkScoreSummaryResponse;
    /** Sidebar use: a smaller disc, and a one-line next-level summary instead of the checklist on phones. */
    condensed?: boolean;
}

/**
 * The Network Score details without a surrounding card: level disc, points, progress bar and the
 * "to reach" checklist. Every row — the points threshold and each of the API's
 * `nextLevelHabitGates` — gets a met/unmet icon, decided by comparing the gate's own
 * `currentValue`/`targetValue`. Shared by the Network home card and the profile.
 */
export const NetworkScoreDetails: React.FC<NetworkScoreDetailsProps> = ({ summary, condensed = false }) => {
    const progressPercent = getNextLevelProgressPercent(summary);
    const atTopLevel = isAtTopLevel(summary);

    return (
        <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative' }}>
                <LevelDisc
                    level={summary.levelName}
                    levelNumber={summary.levelNumber}
                    badgeColourKey={summary.badgeColourKey}
                    size={condensed ? { xs: 56, sm: 88, md: 64, lg: 72 } : 88}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{summary.levelName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {summary.windowPoints} points · last {summary.rollingWindowDays} days
                    </Typography>
                </Box>
            </Stack>

            {!atTopLevel && (
                <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    color="secondary"
                    sx={{ my: 2, height: 8, borderRadius: 999 }}
                />
            )}

            {condensed && (
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                    {atTopLevel
                        ? "You're at the highest level."
                        : summary.windowPoints < summary.nextLevelThreshold
                            ? `${summary.nextLevelThreshold - summary.windowPoints} points to reach ${summary.nextLevelName}`
                            : 'Points target reached · view score for your next steps'}
                </Typography>
            )}
            <Box sx={{ display: condensed ? { xs: 'none', sm: 'block' } : 'block' }}>
                <NetworkScoreChecklist summary={summary} />
            </Box>
        </>
    );
};

export default NetworkScoreDetails;
